import { Replay } from "@mui/icons-material";
import { Alert, Box, Button, Grid, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslate } from "react-admin";

import { LearningMedia } from "../../../lib/learning/contentContract";
import { resolveLearningMediaUrl } from "../../../lib/learning/mediaReference";
import { borderRadius, color, space } from "../../../theme/designTokens";

type MatchingPair = {
  id: string;
  term: string;
  definition: string;
  image?: LearningMedia;
};

type Feedback = "chooseTerm" | "correct" | "incorrect" | null;

export const MatchingBlock = ({ pairs }: { pairs: MatchingPair[] }) => {
  const translate = useTranslate();
  const definitions = useMemo(() => [...pairs].reverse(), [pairs]);
  const [selectedTermId, setSelectedTermId] = useState<string | null>(null);
  const [matchedIds, setMatchedIds] = useState<Set<string>>(() => new Set());
  const [attempts, setAttempts] = useState(0);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const chooseDefinition = (pairId: string): void => {
    if (!selectedTermId) {
      setFeedback("chooseTerm");
      return;
    }
    setAttempts(current => current + 1);
    if (selectedTermId === pairId) {
      setMatchedIds(previous => new Set(previous).add(pairId));
      setFeedback("correct");
    } else {
      setFeedback("incorrect");
    }
    setSelectedTermId(null);
  };

  const reset = (): void => {
    setSelectedTermId(null);
    setMatchedIds(new Set());
    setAttempts(0);
    setFeedback(null);
  };

  const complete = matchedIds.size === pairs.length;

  return (
    <Stack spacing={space.xl}>
      <Typography color="text.secondary">{translate("learning.matching.instructions")}</Typography>
      <Grid container spacing={space.xl}>
        <Grid item xs={12} md={6}>
          <Stack spacing={space.md} aria-label={translate("learning.matching.terms")}>
            {pairs.map(pair => (
              <Button
                key={pair.id}
                data-testid={`learning-matching-term-${pair.id}`}
                variant={selectedTermId === pair.id ? "contained" : "outlined"}
                disabled={matchedIds.has(pair.id)}
                aria-pressed={selectedTermId === pair.id}
                onClick={() => {
                  setSelectedTermId(pair.id);
                  setFeedback(null);
                }}
                sx={{ justifyContent: "flex-start", minHeight: 56 }}
              >
                {pair.term}
              </Button>
            ))}
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <Stack spacing={space.md} aria-label={translate("learning.matching.definitions")}>
            {definitions.map(pair => (
              <Button
                key={pair.id}
                data-testid={`learning-matching-definition-${pair.id}`}
                variant="outlined"
                disabled={matchedIds.has(pair.id)}
                onClick={() => chooseDefinition(pair.id)}
                sx={{ justifyContent: "flex-start", minHeight: 56, textAlign: "left" }}
              >
                <Stack direction="row" alignItems="center" gap={space.md}>
                  {pair.image && (
                    <Box
                      component="img"
                      src={resolveLearningMediaUrl(pair.image.fn)}
                      alt={pair.image.alt ?? ""}
                      sx={{ width: 44, height: 44, objectFit: "cover", borderRadius: borderRadius.sm }}
                    />
                  )}
                  <span>{pair.definition}</span>
                </Stack>
              </Button>
            ))}
          </Stack>
        </Grid>
      </Grid>
      <Box aria-live="polite">
        {complete ? (
          <Alert severity="success">{translate("learning.matching.complete", { attempts })}</Alert>
        ) : feedback ? (
          <Alert severity={feedback === "correct" ? "success" : "info"}>
            {translate(`learning.matching.${feedback}`)}
          </Alert>
        ) : null}
      </Box>
      {(attempts > 0 || complete) && (
        <Button startIcon={<Replay />} onClick={reset} sx={{ alignSelf: "flex-start", color: color.primary }}>
          {translate("learning.matching.reset")}
        </Button>
      )}
    </Stack>
  );
};
